import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import PaymentMethodModal from './PaymentMethodModal';

const CurrentPlanTab = () => {
  const [autoRenew, setAutoRenew] = useState(true);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const currentPlan = {
    name: 'Growth Plan',
    price: 2999,
    billingCycle: 'Monthly',
    nextPaymentDate: '2026-03-08',
    status: 'active'
  };

  const features = [
    { name: 'Business Model Canvas', included: true, usage: null },
    { name: 'Predictive Analysis', included: true, usage: null },
    { name: 'Goal Intelligence', included: true, usage: null },
    { name: 'Advanced Reports', included: true, usage: { current: 12, limit: 50 } },
    { name: 'AI Insights', included: true, usage: { current: 340, limit: 1000 } },
    { name: 'Team Members', included: true, usage: { current: 3, limit: 10 } },
    { name: 'Priority Support', included: true, usage: null },
    { name: 'Custom Integrations', included: false, usage: null }
  ];

  const paymentMethod = {
    type: 'Visa',
    last4: '4242',
    expiry: '12/2027'
  };

  const getUsagePercentage = (current, limit) => {
    return (current / limit) * 100;
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 80) return 'error';
    if (percentage >= 60) return 'warning';
    return 'success';
  };

  return (
    <div className="space-y-6">
      {/* Current Plan Overview */}
      <div className="bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 border border-border rounded-lg p-6 elevation-sm">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Icon name="Package" size={28} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-foreground font-heading mb-1">
                {currentPlan?.name}
              </h3>
              <div className="flex items-center gap-3 text-sm text-muted-foreground font-caption">
                <span>₹{currentPlan?.price?.toLocaleString()}/{currentPlan?.billingCycle}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  Next billing: {new Date(currentPlan?.nextPaymentDate)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" size="default" iconName="Edit" iconPosition="left">
              Change Plan
            </Button>
            <Button variant="destructive-outline" size="default" iconName="XCircle" iconPosition="left">
              Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* Features & Usage */}
      <div className="bg-card border border-border rounded-lg p-6 elevation-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="CheckCircle" size={20} color="var(--color-accent)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground font-heading">
            Plan Features & Usage
          </h3>
        </div>
        <div className="space-y-4">
          {features?.map((feature, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon 
                    name={feature?.included ? 'CheckCircle' : 'XCircle'} 
                    size={16} 
                    color={feature?.included ? 'var(--color-success)' : 'var(--color-muted-foreground)'} 
                  />
                  <span className={`text-sm font-medium font-body ${
                    feature?.included ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {feature?.name}
                  </span>
                </div>
                {feature?.usage && (
                  <span className="text-sm text-muted-foreground font-caption">
                    {feature?.usage?.current} / {feature?.usage?.limit}
                  </span>
                )}
              </div>
              {feature?.usage && (
                <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 rounded-full transition-smooth bg-${
                      getUsageColor(getUsagePercentage(feature?.usage?.current, feature?.usage?.limit))
                    }`}
                    style={{ width: `${getUsagePercentage(feature?.usage?.current, feature?.usage?.limit)}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Payment Method */}
      <div className="bg-card border border-border rounded-lg p-6 elevation-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="CreditCard" size={20} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground font-heading">
              Payment Method
            </h3>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Edit" 
            iconPosition="left"
            onClick={() => setIsPaymentModalOpen(true)}
          >
            Update
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-muted/30 border border-border rounded-lg">
          <div className="w-12 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">{paymentMethod?.type}</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground font-body">
              •••• •••• •••• {paymentMethod?.last4}
            </p>
            <p className="text-xs text-muted-foreground font-caption">
              Expires {paymentMethod?.expiry}
            </p>
          </div>
        </div>
      </div>

      {/* Subscription Controls */}
      <div className="bg-card border border-border rounded-lg p-6 elevation-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="Settings" size={20} color="var(--color-secondary)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground font-heading">
            Subscription Settings
          </h3>
        </div>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-muted/30 border border-border rounded-lg">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground font-body mb-1">
                Auto-Renewal
              </p>
              <p className="text-xs text-muted-foreground font-caption">
                Automatically renew subscription at the end of billing cycle
              </p>
            </div>
            <button
              onClick={() => setAutoRenew(!autoRenew)}
              className={`relative w-12 h-6 rounded-full transition-smooth flex-shrink-0 ${
                autoRenew ? 'bg-success' : 'bg-muted-foreground'
              }`}
            >
              <div
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-smooth ${
                  autoRenew ? 'right-1' : 'left-1'
                }`}
              />
            </button>
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground font-body mb-1">
                Billing Cycle Change
              </p>
              <p className="text-xs text-muted-foreground font-caption mb-3">
                Switch to annual billing and save 20% on your subscription
              </p>
              <Button variant="warning" size="sm" iconName="Calendar" iconPosition="left">
                Switch to Annual
              </Button>
            </div>
          </div>
        </div>
      </div>

      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />
    </div>
  );
};

export default CurrentPlanTab;